import { Box } from '@mui/material';
import type { ReactElement } from 'react';

import { defaultFormat, describeRange, describeSeries, tableRows, type ValueFormatter } from './alternative';
import { ChartFrame } from './ChartFrame';
import { isValue, linearScale, polylinePath, presentRuns, type Maybe } from './geometry';
import { useTokens, type SeriesToken } from './seriesPalette';

/**
 * Mini-courbe d'une tuile d'indicateur (maquette « courbe » de `kpi`).
 *
 * Une serie, sans axe : elle montre une **forme**, pas des valeurs, d'ou une
 * echelle qui va du plus bas au plus haut de la serie et non de zero. Les
 * valeurs, elles, sont dans l'alternative textuelle (phrase et table).
 *
 * **Moins de deux valeurs presentes : rien n'est dessine** — pas de cadre vide,
 * pas de ligne plate qui se lirait « stable ». La tuile (`Metric`) dit deja
 * « — » ou pourquoi, et ne reserve pas de place a une courbe absente.
 */
export interface SparklineProps {
  /** Nom accessible : ce que mesure la courbe (« Saisies par jour »). */
  readonly label: string;
  /** Abscisse de chaque valeur (« 5 sept. »), pour l'alternative textuelle. */
  readonly categories: readonly string[];
  readonly values: readonly Maybe[];
  readonly token?: SeriesToken;
  readonly formatValue?: ValueFormatter;
  readonly width?: number;
  readonly height?: number;
}

const PAD = 3;

export const Sparkline = ({
  label,
  categories,
  values,
  token = 'primary',
  formatValue = defaultFormat,
  width = 96,
  height = 34,
}: SparklineProps): ReactElement | null => {
  const tokens = useTokens();
  const present = values.filter(isValue);

  if (present.length < 2) {
    return null;
  }

  const x = linearScale(0, Math.max(values.length - 1, 1), PAD, width - PAD);
  const y = linearScale(Math.min(...present), Math.max(...present), height - PAD, PAD);
  const color = tokens[token];

  return (
    <ChartFrame
      inline
      label={label}
      summary={`${describeRange(categories)} ${describeSeries(label, categories, values, formatValue)}`}
      table={{ categoryHeader: 'Point', columns: [label], rows: tableRows(categories, [{ values }], formatValue) }}
    >
      <Box component="span" sx={{ display: 'block', width, height }}>
        <svg width={width} height={height} focusable="false" style={{ display: 'block', overflow: 'visible' }}>
          {presentRuns(values).map((run) => {
            const points = run.map((index) => [x(index), y(values[index]!)] as const);
            const first = points[0]!;
            const last = points[points.length - 1]!;

            return run.length === 1 ? (
              <circle key={run[0]} cx={first[0]} cy={first[1]} r={2.5} fill={color} />
            ) : (
              <g key={run[0]}>
                <path
                  d={`${polylinePath(points)} L ${String(last[0])} ${String(height)} L ${String(first[0])} ${String(height)} Z`}
                  fill={color}
                  fillOpacity={0.12}
                />
                <path
                  d={polylinePath(points)}
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            );
          })}
        </svg>
      </Box>
    </ChartFrame>
  );
};
